import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";

export const fetchFaults = createAsyncThunk(
  "calculator/fetchFaults",
  async ({ brandSlug, productSlug, variantSlug }, { rejectWithValue, getState }) => { 
    try {
      const response = await api.getFaults(brandSlug, productSlug, variantSlug);
      return response.data || response;
    } catch (err) {
      return rejectWithValue(
        err.message || "Failed to load device information",
      );
    }
  },
);

export const calculateDevicePrice = createAsyncThunk(
  "calculator/calculateDevicePrice",
  async (
    {brandSlug, productSlug, variationSlug, deviceAge, cityId, faultIds, upgradeIds, optionIds },
    { rejectWithValue, getState },
  ) => {
    // Redux state alone can temporarily be stale after a token is revoked.
    if (!getState().auth?.isLoggedIn || !localStorage.getItem("auth_token")) {
      return rejectWithValue("Please login before calculating the price");
    }
    try {
      const payload = {
        brand_slug: brandSlug,
        product_slug: productSlug,
        variation_slug: variationSlug,
        device_age: deviceAge,
        city_id: parseInt(cityId),
        faults: faultIds || [],
        upgrades: upgradeIds || [],
        option_ids: optionIds || [],
      };

      const response = await api.calculateDevicePrice(payload);

      if (response.success === true) {
        let price = null;
        let evaluationId = null;
        let variationId = null;

        if (response.data) {
          const evaluation = response.data.evaluation;
          const variation = response.data.variation;

          if (evaluation && evaluation.evaluated_amount) {
            price = evaluation.evaluated_amount;
            evaluationId = evaluation.id;
          }

          if (variation && variation.id) {
            variationId = variation.id;
          }
        }

        if (price && evaluationId && variationId) {
          return {
            final_amount: price,
            evaluation_id: evaluationId,
            variation_id: variationId,
            slug: variationSlug,
            selected_faults: faultIds || [],
            selected_options: optionIds || [],
          };
        } else {
          return rejectWithValue("Failed to calculate device value");
        }
      } else {
        return rejectWithValue(response.message || "Failed to calculate price");
      }
    } catch (err) {
      return rejectWithValue(err.message || "Failed to calculate device value");
    }
  },
);

const initialState = {
  productData: null,
  variation: null,
  upgradeOptions: [],
  faultCategories: [],
  selectedUpgrades: [],
  selectedFaults: {},
  selectedOptions: {}, // Track selected options per fault { faultId: optionId }
  deviceAge: "",
  ageRanges: [],
  loading: false,
  calculating: false,
  error: null,
  currentStep: 0,
  stepCategories: [],
  totalSteps: 0,
};

const calculatorSlice = createSlice({
  name: "calculator",
  initialState,
  reducers: {
    setDeviceAge: (state, action) => {
      state.deviceAge = action.payload;
    },
    setSelectedFaults: (state, action) => {
      state.selectedFaults = action.payload;
    },
    // store/slices/calculatorSlice.js - Update toggleFault reducer

    toggleFault: (state, action) => {
      const { faultId, category } = action.payload;
      const selectionType = category?.selection_type || "multiple";

      // Check if this fault has options
      const fault = category?.faults?.find((f) => f.id === faultId);
      const hasOptions = fault && fault.options && fault.options.length > 0;

      if (selectionType === "radio") {
        const categoryFaultIds = category?.faults?.map((f) => f.id) || [];
        const isCurrentlySelected = state.selectedFaults[faultId] === true;

        // If already selected, deselect it (unselect)
        if (isCurrentlySelected) {
          const newSelected = { ...state.selectedFaults };
          delete newSelected[faultId];
          state.selectedFaults = newSelected;

          // Also clear the selected option for this fault
          if (hasOptions) {
            const newOptions = { ...state.selectedOptions };
            delete newOptions[faultId];
            state.selectedOptions = newOptions;
          }
        } else {
          // Select this fault and deselect all others in the category
          const newSelected = { ...state.selectedFaults };
          categoryFaultIds.forEach((id) => {
            delete newSelected[id];
          });
          newSelected[faultId] = true;
          state.selectedFaults = newSelected;

          // If the fault has options, auto-select default option ONLY if one exists
          if (hasOptions) {
            const defaultOption = fault.options.find(
              (opt) => opt.is_default === true,
            );
            if (defaultOption) {
              state.selectedOptions = {
                ...state.selectedOptions,
                [faultId]: defaultOption.id,
              };
            } else {
              // If no default, check if there's already a selected option for this fault
              if (!state.selectedOptions[faultId]) {
                state.selectedOptions = {
                  ...state.selectedOptions,
                  [faultId]: fault.options[0]?.id,
                };
              }
            }
          }
        }
      } else {
        // Checkbox style - just toggle
        const isCurrentlySelected = state.selectedFaults[faultId] === true;

        if (isCurrentlySelected) {
          // Deselect
          const newSelected = { ...state.selectedFaults };
          delete newSelected[faultId];
          state.selectedFaults = newSelected;

          // Clear option if has options
          if (hasOptions) {
            const newOptions = { ...state.selectedOptions };
            delete newOptions[faultId];
            state.selectedOptions = newOptions;
          }
        } else {
          // Select
          state.selectedFaults = {
            ...state.selectedFaults,
            [faultId]: true,
          };

          // If the fault has options and no option is selected, auto-select default
          if (hasOptions && !state.selectedOptions[faultId]) {
            const defaultOption = fault.options.find(
              (opt) => opt.is_default === true,
            );
            if (defaultOption) {
              state.selectedOptions = {
                ...state.selectedOptions,
                [faultId]: defaultOption.id,
              };
            } else if (fault.options.length > 0) {
              state.selectedOptions = {
                ...state.selectedOptions,
                [faultId]: fault.options[0].id,
              };
            }
          }
        }
      }
    },
    toggleOption: (state, action) => {
      const { faultId, optionId } = action.payload;

      // Check if this option is already selected
      const currentSelected = state.selectedOptions[faultId];

      // If the same option is clicked again, deselect it (toggle off)
      if (currentSelected === optionId) {
        // Deselect - remove the option
        const newOptions = { ...state.selectedOptions };
        delete newOptions[faultId];
        state.selectedOptions = newOptions;

        // Also deselect the fault if no option is selected
        const newFaults = { ...state.selectedFaults };
        delete newFaults[faultId];
        state.selectedFaults = newFaults;
      } else {
        // Select the new option
        state.selectedOptions = {
          ...state.selectedOptions,
          [faultId]: optionId,
        };

        // Auto-select the fault if it's not already selected
        if (!state.selectedFaults[faultId]) {
          state.selectedFaults = {
            ...state.selectedFaults,
            [faultId]: true,
          };
        }
      }
    },
    setSelectedOptions: (state, action) => {
      state.selectedOptions = action.payload;
    },
    clearSelectedOptions: (state) => {
      state.selectedOptions = {};
    },
    toggleUpgrade: (state, action) => {
      const upgradeId = action.payload;
      let groupId = null;
      let groupOptions = [];

      for (const group of state.upgradeOptions) {
        if (group.options?.some((opt) => opt.id === upgradeId)) {
          groupId = group.id;
          groupOptions = group.options || [];
          break;
        }
      }

      if (groupId) {
        const groupOptionIds = groupOptions.map((opt) => opt.id);
        state.selectedUpgrades = state.selectedUpgrades.filter(
          (id) => !groupOptionIds.includes(id),
        );
        state.selectedUpgrades.push(upgradeId);
      } else {
        if (state.selectedUpgrades.includes(upgradeId)) {
          state.selectedUpgrades = state.selectedUpgrades.filter(
            (id) => id !== upgradeId,
          );
        } else {
          state.selectedUpgrades = [...state.selectedUpgrades, upgradeId];
        }
      }
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps - 1) {
        state.currentStep += 1;
      }
    },
    prevStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },
    clearCalculator: (state) => {
      Object.assign(state, {
        ...initialState,
        ageRanges: state.ageRanges,
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFaults.fulfilled, (state, action) => {
        state.loading = false;

        const responseData = action.payload;
        const variation =
          responseData?.data?.variation || responseData?.variation || null;
        const upgradeOptions =
          responseData?.data?.upgradeOptions ||
          responseData?.upgradeOptions ||
          [];
        const faultCategories =
          responseData?.data?.faults || responseData?.faults || [];

        state.productData = {
          product_name:
            variation?.product?.title || variation?.title || "Product",
          variation_name: variation?.title || "Variation",
          product_image: variation?.product?.image || "",
          variation: variation,
        };

        state.variation = variation;
        state.upgradeOptions = upgradeOptions;
        state.faultCategories = faultCategories;

        if (variation?.product?.category?.age_ranges) {
          state.ageRanges = variation.product.category.age_ranges;
        }

        // Initialize default options for faults
        const defaultOptions = {};
        const defaultFaults = {};

        faultCategories.forEach((category) => {
          category.faults?.forEach((fault) => {
            // Check if fault has options
            if (fault.options && fault.options.length > 0) {
              // Find default option
              const defaultOption = fault.options.find(
                (opt) => opt.is_default === true,
              );
              if (defaultOption) {
                defaultOptions[fault.id] = defaultOption.id;
                // Auto-select the fault if it has a default option
                defaultFaults[fault.id] = true;
              }
            }
          });
        });

        state.selectedOptions = defaultOptions;
        state.selectedFaults = defaultFaults;

        // Filter out categories with NO faults
        const filteredCategories = faultCategories.filter(
          (cat) => cat.faults && cat.faults.length > 0,
        );

        // Group categories by group_id
        const groupedMap = new Map();
        const ungrouped = [];

        filteredCategories.forEach((category) => {
          if (category.group_id !== null && category.group_id !== undefined) {
            const groupId = category.group_id.toString();
            if (!groupedMap.has(groupId)) {
              groupedMap.set(groupId, []);
            }
            groupedMap.get(groupId).push(category);
          } else {
            ungrouped.push(category);
          }
        });

        const steps = [];
        ungrouped.forEach((category) => {
          steps.push([category]);
        });
        for (const [groupId, categories] of groupedMap) {
          steps.push(categories);
        }

        state.stepCategories = steps;

        const hasUpgrades = upgradeOptions && upgradeOptions.length > 0;
        state.totalSteps = (hasUpgrades ? 1 : 0) + 1 + steps.length;
        state.currentStep = 0;
        state.error = null;
      })
      .addCase(fetchFaults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load device information";
      })
      .addCase(calculateDevicePrice.pending, (state) => {
        state.calculating = true;
        state.error = null;
      })
      .addCase(calculateDevicePrice.fulfilled, (state) => {
        state.calculating = false;
        state.error = null;
      })
      .addCase(calculateDevicePrice.rejected, (state, action) => {
        state.calculating = false;
        state.error = action.payload || "Failed to calculate price";
      });
  },
});

export const {
  setDeviceAge,
  setSelectedFaults,
  toggleFault,
  toggleOption,
  setSelectedOptions,
  clearSelectedOptions,
  toggleUpgrade,
  setCurrentStep,
  nextStep,
  prevStep,
  clearCalculator,
} = calculatorSlice.actions;

export default calculatorSlice.reducer;
